import { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Edit, Trash2, X, Save, MapPin, Search, Loader2, Upload, Download, AlertTriangle, ChevronUp, ChevronDown, Filter, XCircle } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { customerService } from '../services/customerService';
import { warehouseService } from '../services/warehouseService';
import { staticDefinitionService } from '../services/staticDefinitionService';
import { citiesService } from '../services/citiesService';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { WorkiomTablePagination } from '../components/WorkiomTablePagination';
import { toast } from 'sonner';
import { exportRowsToExcel } from '../utils/excelExport';
import { getGoogleMapsApiKeySync } from '../utils/googleMaps';

interface Customer {
  id: number;
  code?: string;
  name: string;
  taxNumber?: string;
  customerType: 'Yükleme' | 'Teslimat' | 'Her İkisi';
  city: string;
  district?: string;
  neighborhood?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  contactPerson?: string;
  phone: string;
  email?: string;
  loadingStartTime?: string;
  loadingEndTime?: string;
  unloadingStartTime?: string;
  unloadingEndTime?: string;
  loadingServiceDurationMinutes?: number;
  unloadingServiceDurationMinutes?: number;
  unloadingWaitDurationMinutes?: number;
  status: 'Aktif' | 'Pasif';
  notes?: string;
  kanalTipi?: string;
}

const normalizeCoordinate = (value: unknown, maxAbs: number) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(/\s+/g, '');
    if (!trimmed) return null;
    value = trimmed.includes(',') && !trimmed.includes('.')
      ? trimmed.replace(',', '.')
      : trimmed.replace(/,/g, '');
  }

  let parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;

  while (Math.abs(parsed) > maxAbs && Number.isInteger(parsed)) {
    parsed /= 10;
  }

  return Math.abs(parsed) <= maxAbs ? parsed : null;
};

const normalizeCustomerKeyPart = (value: unknown) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/\s+/g, ' ')
  .trim();

const buildCustomerImportMatchKey = (row: { code?: unknown; name?: unknown; city?: unknown; district?: unknown; address?: unknown }) => {
  const code = normalizeCustomerKeyPart(row.code);
  if (code) return `code:${code}`;
  return `name:${normalizeCustomerKeyPart(row.name)}|city:${normalizeCustomerKeyPart(row.city)}|district:${normalizeCustomerKeyPart(row.district)}|address:${normalizeCustomerKeyPart(row.address)}`;
};

interface LocationRow {
  id: number;
  name: string;
  name_ascii?: string;
}

interface StokbarPreviewRow {
  sourceCity: string;
  sourceCityAscii: string;
  name: string;
  title?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  district?: string;
  neighborhood?: string;
  resolvedCity?: string;
  resolvedDistrict?: string;
  resolvedNeighborhood?: string;
  code?: string;
  latitude?: number | null;
  longitude?: number | null;
  status: 'ready' | 'error';
  reason?: string;
}

interface StokbarCityProgress {
  cityId: number;
  city: string;
  cityAscii: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  customerCount: number;
  readyCount: number;
  errorCount: number;
  startedAt?: string | null;
  completedAt?: string | null;
  durationMs?: number | null;
  message?: string;
}

const DEFAULT_CHANNEL_OPTIONS = ['DİST', 'EDT', 'E-TİCARET', 'ETİL ALKOL', 'GRUP ŞİRKE', 'LC', 'NA', 'NA-(A101)', 'NA-BİM', 'NA-FİLE', 'NA-PL', 'NA-ŞOK', 'TALEP', 'TOZ ŞEKER', 'Y.İÇİ-PL', 'YDISI'];

export function CustomerManagement() {
  const [data, setData] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Customer | null>(null);
  const [formData, setFormData] = useState<Partial<Customer>>({});
  const [saving, setSaving] = useState(false);
  const [importPreviewRows, setImportPreviewRows] = useState<any[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; updated: number; skipped: Array<{ row: any; reason: string }> } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showLocationFilters, setShowLocationFilters] = useState(false);
  const [sortField, setSortField] = useState<keyof Customer | ''>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<Partial<Record<keyof Customer, string>>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);
  const [groupBy, setGroupBy] = useState<string>('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [coordinateAuditOpen, setCoordinateAuditOpen] = useState(false);
  const [coordinateAuditLoading, setCoordinateAuditLoading] = useState(false);
  const [coordinateAuditRows, setCoordinateAuditRows] = useState<any[]>([]);
  const [coordinateAuditJobId, setCoordinateAuditJobId] = useState<string | null>(null);
  const [coordinateAuditProgress, setCoordinateAuditProgress] = useState(0);
  const [coordinateAuditProcessed, setCoordinateAuditProcessed] = useState(0);
  const [coordinateAuditTotal, setCoordinateAuditTotal] = useState(0);
  const [cities, setCities] = useState<LocationRow[]>([]);
  const [districts, setDistricts] = useState<LocationRow[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<LocationRow[]>([]);
  const [locationDirectoryAvailable, setLocationDirectoryAvailable] = useState(true);
  const [selectedCity, setSelectedCity] = useState<LocationRow | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<LocationRow | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [stokbarTransferLoading, setStokbarTransferLoading] = useState(false);
  const [channelOptions, setChannelOptions] = useState<string[]>([]);
  const [showStokbarPreviewModal, setShowStokbarPreviewModal] = useState(false);
  const [stokbarPreviewRows, setStokbarPreviewRows] = useState<StokbarPreviewRow[]>([]);
  const [stokbarPreviewSummary, setStokbarPreviewSummary] = useState<{ total: number; ready: number; error: number; uniqueCustomers?: number } | null>(null);
  const [stokbarImporting, setStokbarImporting] = useState(false);
  const [stokbarPreviewJobId, setStokbarPreviewJobId] = useState<string | null>(null);
  const [stokbarPreviewJobStatus, setStokbarPreviewJobStatus] = useState<'queued' | 'running' | 'completed' | 'failed' | null>(null);
  const [stokbarCityProgress, setStokbarCityProgress] = useState<StokbarCityProgress[]>([]);
  const [importParsing, setImportParsing] = useState(false);
  const [parseStatus, setParseStatus] = useState('');
  const [selectedStokbarCity, setSelectedStokbarCity] = useState('');
  const [updatingChannelCustomerId, setUpdatingChannelCustomerId] = useState<number | null>(null);
  const [osmUpdating, setOsmUpdating] = useState(false);
  const [osmProgress, setOsmProgress] = useState({ total: 0, current: 0, success: 0, failed: 0 });
  const excelImportInputRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);

  const handleExcelUploadClick = () => {
    if (importParsing) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,.csv';
    input.style.display = 'none';
    const onChange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0] || null;
      input.removeEventListener('change', onChange);
      input.remove();
     // alert('TEST: dosya seçildi: ' + (file ? file.name : 'YOK'));
      if (file) handleImport(file);
    };
    input.addEventListener('change', onChange);
    document.body.appendChild(input);
    input.click();
  };

  useEffect(() => {
    if (!isFormOpen || !addressInputRef.current) return;
    let cancelled = false;

    const loadScript = async () => {
      if ((window as any).google?.maps?.places) return;
      const key = await import('../utils/googleMaps').then(m => m.getGoogleMapsApiKey());
      if (!key) return;
      return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Google Maps yüklenemedi'));
        document.head.appendChild(script);
      });
    };

    loadScript().then(() => {
      if (cancelled || !addressInputRef.current || !(window as any).google?.maps?.places) return;
      const autocomplete = new (window as any).google.maps.places.Autocomplete(addressInputRef.current, {
        fields: ['formatted_address', 'geometry', 'address_components', 'name'],
        componentRestrictions: { country: 'tr' },
      });
      autocompleteRef.current = autocomplete;
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place) return;
        const components = place?.address_components || [];
        const getPart = (type: string) => components.find((c: any) => c.types.includes(type))?.long_name || '';
        const city = getPart('administrative_area_level_1') || getPart('locality');
        const district = getPart('administrative_area_level_2') || getPart('sublocality_level_1');
        setFormData((current) => ({
          ...current,
          address: place?.formatted_address || place?.name || current.address || '',
          city: city || current.city || '',
          district: district || current.district || '',
          latitude: place?.geometry?.location?.lat ? String(place.geometry.location.lat()) : current.latitude || '',
          longitude: place?.geometry?.location?.lng ? String(place.geometry.location.lng()) : current.longitude || '',
        }));
      });
    }).catch(() => toast.error('Google Places yüklenemedi'));

    return () => { cancelled = true; autocompleteRef.current = null; };
  }, [isFormOpen]);

  useEffect(() => {
    loadCustomers();
    loadCities();
    loadChannelOptions();
  }, []);

  const loadChannelOptions = async () => {
    try {
      const response: any = await staticDefinitionService.getByKey('customer_channel');
      const items = Array.isArray(response?.data?.items) ? response.data.items : [];
      setChannelOptions(items.map((item: any) => item.value).filter(Boolean));
    } catch {
      setChannelOptions([]);
    }
  };

 // useAutoRefresh(() => loadCustomers().catch((error) => console.error('Customer auto refresh failed:', error)), { intervalMs: 30000 });

  useEffect(() => {
    if (!stokbarPreviewJobId || !showStokbarPreviewModal) return;

    let cancelled = false;
    let timeoutId: number | null = null;

    const poll = async () => {
      try {
        const response: any = await customerService.getPreviewTransferFromStokbarStatus(stokbarPreviewJobId);
        if (cancelled || !response?.success) return;
        const payload = response.data || {};
        setStokbarPreviewJobStatus(payload.status || null);
        setStokbarCityProgress(Array.isArray(payload.cityProgress) ? payload.cityProgress : []);
        if (payload.summary) {
          setStokbarPreviewSummary({
            total: payload.summary.total || 0,
            ready: payload.summary.ready || 0,
            error: payload.summary.error || 0,
          });
        }
        if (Array.isArray(payload.rows)) {
          setStokbarPreviewRows(payload.rows);
        }

        if (payload.status === 'queued' || payload.status === 'running') {
          timeoutId = window.setTimeout(poll, 1500);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error polling Stokbar preview status:', error);
          timeoutId = window.setTimeout(poll, 2500);
        }
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [stokbarPreviewJobId, showStokbarPreviewModal]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerService.getCustomers();
      
      if (response.success && response.data) {
        const mappedData: Customer[] = response.data.map((c: any) => ({
          id: c.id,
          code: c.code || '',
          name: c.name || c.company_name || '',
          taxNumber: c.tax_number || c.vkn || '',
          customerType: c.customer_type || c.type || 'Her İkisi',
          city: c.city || c.il || '',
          district: c.district || c.ilce || '',
          neighborhood: c.neighborhood || c.mahalle || '',
          address: c.address || c.adres || '',
          latitude: c.latitude || c.enlem || '',
          longitude: c.longitude || c.boylam || '',
          contactPerson: c.contact_person || c.yetkili || '',
          phone: c.phone || c.telefon || '',
          email: c.email || c.eposta || '',
          loadingStartTime: c.loading_start_time || '',
          loadingEndTime: c.loading_end_time || '',
          unloadingStartTime: c.unloading_start_time || '',
          unloadingEndTime: c.unloading_end_time || '',
          loadingServiceDurationMinutes: Number(c.loading_service_duration_minutes || 90),
          unloadingServiceDurationMinutes: Number(c.unloading_service_duration_minutes || 90),
          unloadingWaitDurationMinutes: Number(c.unloading_wait_duration_minutes || 0),
          status: c.status === 'active' || c.status === 'Aktif' ? 'Aktif' : 'Pasif',
          notes: c.notes || c.notlar || '',
          kanalTipi: c.kanal_tipi || '',
        }));
        setData(mappedData);
      } else {
        toast.error(response.message || 'Müşteriler yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Müşteriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      setLocationLoading(true);
      const response: any = await citiesService.getAll();
      if (response?.success && Array.isArray(response.data)) {
        setCities(response.data);
        setLocationDirectoryAvailable(true);
      }
    } catch (error) {
      console.error('Error loading cities:', error);
      setLocationDirectoryAvailable(false);
    } finally {
      setLocationLoading(false);
    }
  };

  const handleCitySelect = async (city: LocationRow) => {
    try {
      setSelectedCity(city);
      setSelectedDistrict(null);
      setNeighborhoods([]);
      setLocationLoading(true);
      const response: any = await citiesService.getDistricts(city.id);
      setDistricts(response?.success && Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading districts:', error);
      setDistricts([]);
    } finally {
      setLocationLoading(false);
    }
  };

  const handleDistrictSelect = async (district: LocationRow) => {
    try {
      setSelectedDistrict(district);
      setLocationLoading(true);
      const response: any = await citiesService.getNeighborhoods(district.id);
      setNeighborhoods(response?.success && Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading neighborhoods:', error);
      setNeighborhoods([]);
    } finally {
      setLocationLoading(false);
    }
  };

  const applyLocationFilters = () => {
    setFilters((current) => ({
      ...current,
      city: selectedCity?.name || '',
      district: selectedDistrict?.name || '',
    }));
  };

  const clearLocationFilters = () => {
    setSelectedCity(null);
    setSelectedDistrict(null);
    setDistricts([]);
    setNeighborhoods([]);
    setFilters((current) => ({
      ...current,
      city: '',
      district: '',
      neighborhood: '',
    }));
  };

  const handleTransferFromStokbar = async () => {
    try {
      setStokbarTransferLoading(true);
      const response: any = await customerService.previewTransferFromStokbar(selectedStokbarCity || undefined);
      if (!response?.success) {
        toast.error(response?.message || 'Stokbar aktarımı başarısız');
        return;
      }

      setStokbarPreviewJobId(response.data?.jobId || null);
      setStokbarPreviewJobStatus(response.data?.status || 'queued');
      setStokbarCityProgress(Array.isArray(response.data?.cityProgress) ? response.data.cityProgress : []);
      setStokbarPreviewRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
      setStokbarPreviewSummary(response.data?.summary ? {
        total: response.data.summary.total || 0,
        ready: response.data.summary.ready || 0,
        error: response.data.summary.error || 0,
        uniqueCustomers: response.data.summary.uniqueCustomers || 0,
      } : { total: 0, ready: 0, error: 0, uniqueCustomers: 0 });
      setShowStokbarPreviewModal(true);
    } catch (error: any) {
      console.error('Error transferring from Stokbar:', error);
      toast.error(error?.message || 'Stokbar aktarımı sırasında hata oluştu');
    } finally {
      setStokbarTransferLoading(false);
    }
  };

  const handleImportStokbarPreview = async () => {
    try {
      setStokbarImporting(true);
      const readyRows = stokbarPreviewRows.filter((row) => row.status === 'ready');
      if (readyRows.length === 0) {
        toast.error('Aktarılacak hazır kayıt bulunamadı');
        return;
      }

      const response: any = await customerService.importPreviewTransferFromStokbar(readyRows);
      if (!response?.success) {
        toast.error(response?.message || 'Stokbar müşteri aktarımı başarısız');
        return;
      }

      const result = response.data || {};
      toast.success(`${result.created || 0} yeni, ${result.updated || 0} güncel müşteri aktarıldı`);
      setShowStokbarPreviewModal(false);
      setStokbarPreviewJobId(null);
      setStokbarPreviewJobStatus(null);
      setStokbarCityProgress([]);
      setStokbarPreviewRows([]);
      setStokbarPreviewSummary(null);
      await loadCustomers();
    } catch (error: any) {
      console.error('Error importing Stokbar preview:', error);
      toast.error(error?.message || 'Stokbar önizleme aktarımı sırasında hata oluştu');
    } finally {
      setStokbarImporting(false);
    }
  };

  const toggleSelect = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredData.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredData.map(c => c.id)));
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`${selectedIds.size} müşteri silinsin mi?`)) return;
    
    try {
      setLoading(true);
      for (const id of selectedIds) {
        await customerService.deleteCustomer(id);
      }
      toast.success(`${selectedIds.size} müşteri silindi`);
      setSelectedIds(new Set());
      loadCustomers();
    } catch (err) {
      toast.error('Silme hatası');
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateStatus = async (status: 'Aktif' | 'Pasif') => {
    if (selectedIds.size === 0) return;
    
    try {
      setLoading(true);
      for (const id of selectedIds) {
        await customerService.updateCustomer(id, { status });
      }
      toast.success(`${selectedIds.size} müşteri ${status} yapıldı`);
      setSelectedIds(new Set());
      loadCustomers();
    } catch (err) {
      toast.error('Güncelleme hatası');
    } finally {
      setLoading(false);
    }
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const isCoordinateMissing = (customer: Customer) => {
    const lat = normalizeCoordinate(customer.latitude, 90);
    const lng = normalizeCoordinate(customer.longitude, 180);
    return lat === null || lng === null || lat === 0 || lng === 0;
  };

  const findCoordinatesFromOSM = async (customer: Customer) => {
    const city = String(customer.city || '').trim();
    const district = String(customer.district || '').trim();

    if (!city || !district) return null;

    const queries = [
      `${district}, ${city}, Türkiye`,
      `${district} ${city} Türkiye`,
    ];

    for (const query of queries) {
      const url =
        `https://nominatim.openstreetmap.org/search?` +
        `format=json&limit=1&countrycodes=tr&q=${encodeURIComponent(query)}`;

      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) continue;

      const result = await response.json();
      if (!Array.isArray(result) || result.length === 0) continue;

      const latitude = normalizeCoordinate(result[0].lat, 90);
      const longitude = normalizeCoordinate(result[0].lon, 180);

      if (latitude !== null && longitude !== null) {
        return {
          latitude: String(latitude),
          longitude: String(longitude),
        };
      }
    }

    return null;
  };

  const handleUpdateMissingCoordinatesFromOSM = async () => {
    const customersWithoutCoordinates = data.filter((customer) =>
      isCoordinateMissing(customer) &&
      String(customer.city || '').trim() &&
      String(customer.district || '').trim()
    );

    if (customersWithoutCoordinates.length === 0) {
      toast.info('Enlem/boylamı eksik ve il-ilçe bilgisi olan müşteri bulunamadı');
      return;
    }

    if (!confirm(`${customersWithoutCoordinates.length} müşterinin eksik koordinatı OpenStreetMap ile güncellensin mi?`)) {
      return;
    }

    let success = 0;
    let failed = 0;

    try {
      setOsmUpdating(true);
      setOsmProgress({ total: customersWithoutCoordinates.length, current: 0, success: 0, failed: 0 });

      for (let i = 0; i < customersWithoutCoordinates.length; i++) {
        const customer = customersWithoutCoordinates[i];
        setOsmProgress({ total: customersWithoutCoordinates.length, current: i + 1, success, failed });

        try {
          const coords = await findCoordinatesFromOSM(customer);

          if (!coords) {
            failed += 1;
            setOsmProgress({ total: customersWithoutCoordinates.length, current: i + 1, success, failed });
            await sleep(1100);
            continue;
          }

          await customerService.updateCustomer(customer.id, {
            latitude: coords.latitude,
            longitude: coords.longitude,
          });

          success += 1;
          setData((prev) => prev.map((item) => item.id === customer.id ? {
            ...item,
            latitude: coords.latitude,
            longitude: coords.longitude,
          } : item));
          setOsmProgress({ total: customersWithoutCoordinates.length, current: i + 1, success, failed });
          await sleep(1100);
        } catch (error) {
          console.error('OSM koordinat güncelleme hatası:', customer, error);
          failed += 1;
          setOsmProgress({ total: customersWithoutCoordinates.length, current: i + 1, success, failed });
          await sleep(1100);
        }
      }

      toast.success(`${success} müşteri güncellendi, ${failed} müşteri bulunamadı`);
      await loadCustomers();
    } catch (error) {
      console.error('Toplu OSM koordinat güncelleme hatası:', error);
      toast.error('Koordinat güncelleme sırasında hata oluştu');
    } finally {
      setOsmUpdating(false);
    }
  };

  const applySuggestedCoordinates = async (row: any) => {
    const latitude = row.suggestedLatitude ?? row.expectedLatitude;
    const longitude = row.suggestedLongitude ?? row.expectedLongitude;
    if (latitude == null || longitude == null) {
      toast.error('Bu kayıt için önerilen koordinat bulunamadı');
      return;
    }
    try {
      if (row.type === 'warehouse') {
        await warehouseService.updateWarehouse(row.id, { latitude: String(latitude), longitude: String(longitude) });
      } else {
        await customerService.updateCustomer(row.id, { latitude: String(latitude), longitude: String(longitude) });
      }
      setCoordinateAuditRows((prev) => prev.map((item) => item.type === row.type && String(item.id) === String(row.id)
        ? { ...item, currentLatitude: latitude, currentLongitude: longitude, differenceKm: 0, districtDifferenceKm: 0, status: 'ok' }
        : item));
      toast.success('Önerilen koordinat uygulandı');
      await loadCustomers();
    } catch (error) {
      console.error('Apply suggested coordinates error:', error);
      toast.error('Önerilen koordinat uygulanamadı');
    }
  };

  const columns = [
    { key: 'name', label: 'Müşteri Adı' },
    { key: 'code', label: 'Cari Kodu' },
    { key: 'taxNumber', label: 'VKN' },
    { key: 'customerType', label: 'Müşteri Tipi' },
    { key: 'city', label: 'İl' },
    { key: 'district', label: 'İlçe' },
    { key: 'neighborhood', label: 'Mahalle' },
    { key: 'latitude', label: 'Enlem' },
    { key: 'longitude', label: 'Boylam' },
    { key: 'contactPerson', label: 'Yetkili' },
    { key: 'phone', label: 'Telefon' },
    { key: 'kanalTipi', label: 'Kanal Tipi' },
    { key: 'status', label: 'Durum' },
  ];

  const openForm = (item?: Customer) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData({ status: 'Aktif', customerType: 'Her İkisi', kanalTipi: '', loadingServiceDurationMinutes: 90, unloadingServiceDurationMinutes: 90, unloadingWaitDurationMinutes: 0 });
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
    setFormData({});
    autocompleteRef.current = null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const customerData = {
        name: formData.name,
        code: formData.code,
        tax_number: formData.taxNumber,
        type: formData.customerType,
        city: formData.city,
        district: formData.district,
        neighborhood: formData.neighborhood,
        address: formData.address,
        latitude: formData.latitude,
        longitude: formData.longitude,
        loading_start_time: formData.loadingStartTime || null,
        loading_end_time: formData.loadingEndTime || null,
        unloading_start_time: formData.unloadingStartTime || null,
        unloading_end_time: formData.unloadingEndTime || null,
        loading_service_duration_minutes: Number(formData.loadingServiceDurationMinutes || 90),
        unloading_service_duration_minutes: Number(formData.unloadingServiceDurationMinutes || 90),
        unloading_wait_duration_minutes: Number(formData.unloadingWaitDurationMinutes || 0),
        contact_person: formData.contactPerson,
        phone: formData.phone,
        email: formData.email,
        status: formData.status === 'Aktif' ? 'active' : 'inactive',
        notes: formData.notes,
        kanal_tipi: formData.kanalTipi || null,
      };

      let response;
      
      if (editingItem) {
        response = await customerService.updateCustomer(editingItem.id, customerData);
        if (response.success) {
          toast.success('Müşteri güncellendi');
          setData(data.map(item => item.id === editingItem.id ? { ...item, ...formData } as Customer : item));
        } else {
          toast.error(response.message || 'Güncelleme başarısız');
        }
      } else {
        response = await customerService.createCustomer(customerData);
        if (response.success && response.data) {
          toast.success('Müşteri oluşturuldu');
          const newCustomer: Customer = {
            id: response.data.id || Date.now(),
            code: formData.code || '',
            name: formData.name || '',
            taxNumber: formData.taxNumber,
            customerType: formData.customerType || 'Her İkisi',
            city: formData.city || '',
            district: formData.district,
            neighborhood: formData.neighborhood,
            address: formData.address,
            latitude: formData.latitude,
            longitude: formData.longitude,
            contactPerson: formData.contactPerson,
            phone: formData.phone || '',
            email: formData.email,
            loadingStartTime: formData.loadingStartTime,
            loadingEndTime: formData.loadingEndTime,
            unloadingStartTime: formData.unloadingStartTime,
            unloadingEndTime: formData.unloadingEndTime,
            loadingServiceDurationMinutes: Number(formData.loadingServiceDurationMinutes || 90),
            unloadingServiceDurationMinutes: Number(formData.unloadingServiceDurationMinutes || 90),
            unloadingWaitDurationMinutes: Number(formData.unloadingWaitDurationMinutes || 0),
            status: formData.status || 'Aktif',
            notes: formData.notes,
            kanalTipi: formData.kanalTipi || '',
          };
          setData([...data, newCustomer]);
        } else {
          toast.error(response.message || 'Oluşturma başarısız');
        }
      }
      
      closeForm();
    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Bu müşteriyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await customerService.deleteCustomer(id);
      if (response.success) {
        toast.success('Müşteri silindi');
        setData(data.filter(item => item.id !== id));
      } else {
        toast.error(response.message || 'Silme başarısız');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Silme hatası');
    }
  };

const handleImport = (file: File | null) => {
  console.log('handleImport çalıştı:', file);

  if (!file) {
    toast.error('Dosya seçilmedi');
    return;
  }

  setImportParsing(true);
  setParseStatus('Dosya okunuyor...');

  const reader = new FileReader();

  reader.onload = (e) => {
    try {
      setParseStatus('Excel çözümleniyor...');

      const result = e.target?.result;

      if (!result) {
        throw new Error('Dosya içeriği okunamadı');
      }

      const data = new Uint8Array(result as ArrayBuffer);

      console.log('Binary data:', data.length);

      const workbook = XLSX.read(data, {
        type: 'array',
      });

      console.log('Workbook:', workbook);

      if (!workbook.SheetNames.length) {
        throw new Error('Excel sayfası bulunamadı');
      }

      const firstSheetName = workbook.SheetNames[0];

      console.log('İlk sheet:', firstSheetName);

      const sheet = workbook.Sheets[firstSheetName];

      if (!sheet) {
        throw new Error('Sheet okunamadı');
      }

      const json = XLSX.utils.sheet_to_json(sheet, {
        defval: '',
      }) as any[];

      console.log('Excel JSON:', json);

      if (!json.length) {
        toast.error('Excel içinde veri yok');
        return;
      }

      const existingCustomerKeys = new Set(data.map((item) => buildCustomerImportMatchKey({
        code: item.code,
        name: item.name,
        city: item.city,
        district: item.district,
        address: item.address,
      })));
      const seenImportKeys = new Set<string>();

      const preview = json.map((row, index) => {
        const code = String(
          row.code ||
          row['Müşteri Kodu'] ||
          row['Customer Code'] ||
          row.SEVKIYAT_KODU ||
          ''
        ).trim();

        const name = String(
          row.name ||
          row['Müşteri Adı'] ||
          row.companyName ||
          row['Company Name'] ||
          row.SEVKIYAT_ADI ||
          ''
        ).trim();

        const city = String(
          row.city ||
          row['İl'] ||
          row.IL ||
          row.SEHIR ||
          ''
        ).trim();

        const district = String(
          row.district ||
          row['İlçe'] ||
          row.ILCE ||
          row.SEMT ||
          ''
        ).trim();

        const phone = String(
          row.phone ||
          row['Telefon'] ||
          ''
        ).trim();

        const email = String(
          row.email ||
          row['E-posta'] ||
          row.EMAIL ||
          ''
        ).trim();

        const address = String(
          row.address ||
          row['Adres'] ||
          ''
        ).trim();

        const reason = !name || !city ? 'Müşteri adı ve il zorunludur' : '';
        const matchKey = buildCustomerImportMatchKey({ code, name, city, district, address });
        const existsInSystem = existingCustomerKeys.has(matchKey);
        const existsEarlierInFile = seenImportKeys.has(matchKey);
        if (matchKey) seenImportKeys.add(matchKey);
        const importAction = reason ? 'error' : (existsInSystem || existsEarlierInFile ? 'update' : 'create');
        const importActionLabel = importAction === 'create'
          ? 'Yeni Kayıt'
          : importAction === 'update'
            ? (existsEarlierInFile ? 'Aynı Dosyada Tekrar / Güncelleyecek' : 'Mevcut Kaydı Güncelleyecek')
            : 'Hatalı';

        return {
          line: index + 2,
          code,
          name,
          city,
          district,
          phone,
          email,
          customerType:
            String(
              row.customerType ||
              row['Müşteri Tipi'] ||
              'Her İkisi'
            ).trim() || 'Her İkisi',
          status: reason ? 'error' : 'ready',
          reason,
          address,
          importAction,
          importActionLabel,
          raw: row,
        };
      });

      console.log('Preview:', preview);

      setImportPreviewRows(preview);

      setImportResult(null);

      setParseStatus(`${preview.length} satır hazır`);

      setShowImportModal(true);

      toast.success(`${preview.length} satır başarıyla okundu`);
    } catch (error) {
      console.error('Excel parse error:', error);

      alert(
        'Excel parse hatası:\n' +
        (error instanceof Error
          ? error.message
          : String(error))
      );

      toast.error('Excel dosyası okunamadı');
    } finally {
      setImportParsing(false);

      setTimeout(() => {
        setParseStatus('');
      }, 3000);
    }
  };

  reader.onerror = (err) => {
    console.error('FileReader error:', err);

    setImportParsing(false);

    setParseStatus('');

    toast.error('Dosya okunamadı');
  };

  reader.readAsArrayBuffer(file);
};


  const fallbackImportCustomers = async (rows: any[]) => {
    const existingResponse: any = await customerService.getCustomers();
    const existingCustomers = Array.isArray(existingResponse?.data) ? existingResponse.data : [];
    let imported = 0;
    let updated = 0;
    const skipped: Array<{ row: any; reason: string }> = [];

    for (const row of rows) {
      const code = String(row.code || row['Müşteri Kodu'] || row['Customer Code'] || row.SEVKIYAT_KODU || '').trim();
      const name = String(row.name || row['Müşteri Adı'] || row.companyName || row['Company Name'] || row.SEVKIYAT_ADI || '').trim();
      const city = String(row.city || row['İl'] || row.IL || row.SEHIR || '').trim();
      const district = String(row.district || row['İlçe'] || row.ILCE || row.SEMT || '').trim();
      const neighborhood = String(row.neighborhood || row['Mahalle'] || row.MAHALLE || '').trim();
      const taxNumber = String(row.taxNumber || row.VKN || row['Vergi No'] || '').replace(/\D/g, '');
      const contactPerson = String(row.contactPerson || row['Yetkili'] || '').trim();
      const phone = String(row.phone || row['Telefon'] || '').trim();
      const email = String(row.email || row['E-posta'] || row['EMAİL'] || row.EMAIL || '').trim();
      const address = String(row.address || row['Adres'] || '').trim();
      const latitude = normalizeCoordinate(row.latitude ?? row['Enlem'] ?? row.ENLEM ?? null, 90);
      const longitude = normalizeCoordinate(row.longitude ?? row['Boylam'] ?? row.BOYLAM ?? null, 180);
      const customerType = String(row.customerType || row['Müşteri Tipi'] || 'Her İkisi').trim() || 'Her İkisi';
      const loadingStartTime = row.loadingStartTime || row['Yükleme Başlangıç'] || row['Çalışma Başlangıç'] || null;
      const loadingEndTime = row.loadingEndTime || row['Yükleme Bitiş'] || row['Çalışma Bitiş'] || null;
      const unloadingStartTime = row.unloadingStartTime || row['Tahliye Başlangıç'] || null;
      const unloadingEndTime = row.unloadingEndTime || row['Tahliye Bitiş'] || null;
      const loadingServiceDurationMinutes = Number(row.loadingServiceDurationMinutes || row['Yükleme Servis Süresi'] || row['Yükleme Servis Süresi (dk)'] || 90) || 90;
      const unloadingServiceDurationMinutes = Number(row.unloadingServiceDurationMinutes || row['Tahliye Servis Süresi'] || row['Tahliye Servis Süresi (dk)'] || 90) || 90;
      const unloadingWaitDurationMinutes = Number(row.unloadingWaitDurationMinutes || row['Boşaltma Bekleme Süresi'] || row['Boşaltma Bekleme Süresi (dk)'] || 0) || 0;
      const status = String(row.status || row['Durum'] || 'Aktif').trim();
      const notes = String(row.notes || row['Notlar'] || '').trim();
      const kanalTipi = String(row.kanalTipi || row.kanal_tipi || row['Kanal Tipi'] || '').trim();

      if (!name || !city) {
        skipped.push({ row, reason: 'Müşteri adı ve il zorunludur' });
        continue;
      }

      const existing = existingCustomers.find((item: any) =>
        String(item.code || '').trim() === code
        || (
          String(item.company_name || item.name || '').trim() === name
          && String(item.city || '').trim() === city
          && String(item.district || '').trim() === district
        )
      );

      const payload = {
        code: code || null,
        name,
        taxNumber: taxNumber || null,
        contactPerson: contactPerson || null,
        phone: phone || null,
        city,
        district: district || null,
        neighborhood: neighborhood || null,
        address: address || null,
        email: email || null,
        latitude: latitude || null,
        longitude: longitude || null,
        customerType,
        loadingStartTime: loadingStartTime || null,
        loadingEndTime: loadingEndTime || null,
        unloadingStartTime: unloadingStartTime || null,
        unloadingEndTime: unloadingEndTime || null,
        loadingServiceDurationMinutes,
        unloadingServiceDurationMinutes,
        unloadingWaitDurationMinutes,
        status: status === 'Pasif' ? 'inactive' : 'active',
        notes: notes || null,
        kanalTipi: kanalTipi || null,
      };

      if (existing?.id) {
        await customerService.updateCustomer(existing.id, payload);
        updated += 1;
      } else {
        await customerService.createCustomer(payload);
        imported += 1;
      }
    }

    return { imported, updated, skipped };
  };

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      {
        'Müşteri Kodu': '10',
        'Müşteri Adı': 'YEMEK SEPETI - AVRUPA DC MERKEZ DEPO',
        'Müşteri Tipi': 'Her İkisi',
        'Vergi No': '1234567890',
        'İl': 'İstanbul',
        'İlçe': 'Esenyurt',
        'Mahalle': 'Akçaburgaz',
        'Adres': 'Ornek Mah. Lojistik Cad. No:10',
        'Telefon': '05321234567',
        'E-posta': 'ornek@doguscay.com.tr',
        'Yetkili': 'Operasyon Sorumlusu',
        'Kanal Tipi': 'Modern Kanal',
        'Enlem': '41.0612688',
        'Boylam': '28.64211627',
        'Yükleme Başlangıç': '08:00',
        'Yükleme Bitiş': '17:00',
        'Yükleme Servis Süresi (dk)': 90,
        'Tahliye Başlangıç': '09:00',
        'Tahliye Bitiş': '18:00',
        'Tahliye Servis Süresi (dk)': 90,
        'Boşaltma Bekleme Süresi (dk)': 0,
        'Durum': 'Aktif',
        'Notlar': 'Ornek musteri kaydi',
      },
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Müşteriler');
    XLSX.writeFile(workbook, 'musteriler-sablon.xlsx');
  };

  const filteredData = useMemo(() => {
    let result = data.filter(item => {
      const normalizedSearch = searchTerm.toLowerCase();
      const searchMatch = 
        item.name.toLowerCase().includes(normalizedSearch) ||
        (item.code && item.code.toLowerCase().includes(normalizedSearch)) ||
        (item.city && item.city.toLowerCase().includes(normalizedSearch)) ||
        (item.phone && item.phone.includes(searchTerm));
      
      const filterMatch = Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        const itemValue = item[key as keyof Customer];
        return itemValue?.toString().toLowerCase().includes(value.toLowerCase());
      });
      
      return searchMatch && filterMatch;
    });

    if (sortField) {
      result.sort((a, b) => {
        const aVal = a[sortField] || '';
        const bVal = b[sortField] || '';
        const comparison = String(aVal).localeCompare(String(bVal), 'tr');
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, searchTerm, filters, sortField, sortOrder]);

  const cityFilterOptions = useMemo(() => [...new Set(data.map((item) => String(item.city || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr')), [data]);
  const districtFilterOptions = useMemo(() => {
    const selectedCityFilter = String(filters.city || '').trim();
    return [...new Set(data
      .filter((item) => !selectedCityFilter || String(item.city || '').trim() === selectedCityFilter)
      .map((item) => String(item.district || '').trim())
      .filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [data, filters.city]);
  const neighborhoodFilterOptions = useMemo(() => {
    const selectedCityFilter = String(filters.city || '').trim();
    const selectedDistrictFilter = String(filters.district || '').trim();
    return [...new Set(data
      .filter((item) => (!selectedCityFilter || String(item.city || '').trim() === selectedCityFilter) && (!selectedDistrictFilter || String(item.district || '').trim() === selectedDistrictFilter))
      .map((item) => String(item.neighborhood || '').trim())
      .filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [data, filters.city, filters.district]);

  const handleSort = (field: keyof Customer) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;
  const groupedCustomers = useMemo(() => {
    if (!groupBy) return { all: filteredData };
    return filteredData.reduce((acc: Record<string, Customer[]>, item) => {
      const key = String((item as any)[groupBy] || '-');
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [filteredData, groupBy]);

  const handleExportExcel = () => exportRowsToExcel(filteredData as any[], `customer-management-${new Date().toISOString().slice(0, 10)}.xlsx`, {
    sheetName: 'Customers',
    columns: [
      { key: 'code', label: 'Müşteri Kodu' },
      { key: 'name', label: 'Müşteri Adı' },
      { key: 'customerType', label: 'Müşteri Tipi' },
      { key: 'taxNumber', label: 'Vergi No' },
      { key: 'city', label: 'İl' },
      { key: 'district', label: 'İlçe' },
      { key: 'neighborhood', label: 'Mahalle' },
      { key: 'address', label: 'Adres' },
      { key: 'phone', label: 'Telefon' },
      { key: 'email', label: 'E-posta' },
      { key: 'contactPerson', label: 'Yetkili' },
      { key: 'kanalTipi', label: 'Kanal Tipi' },
      { key: 'latitude', label: 'Enlem' },
      { key: 'longitude', label: 'Boylam' },
      { key: 'loadingStartTime', label: 'Yükleme Başlangıç' },
      { key: 'loadingEndTime', label: 'Yükleme Bitiş' },
      { key: 'loadingServiceDurationMinutes', label: 'Yükleme Servis Süresi (dk)' },
      { key: 'unloadingStartTime', label: 'Tahliye Başlangıç' },
      { key: 'unloadingEndTime', label: 'Tahliye Bitiş' },
      { key: 'unloadingServiceDurationMinutes', label: 'Tahliye Servis Süresi (dk)' },
      { key: 'unloadingWaitDurationMinutes', label: 'Boşaltma Bekleme Süresi (dk)' },
      { key: 'status', label: 'Durum' },
      { key: 'notes', label: 'Notlar' },
    ],
  });

  const renderCell = (item: Customer, key: string) => {
    if (key === 'kanalTipi') {
      const options = channelOptions.length > 0 ? channelOptions : DEFAULT_CHANNEL_OPTIONS;
      return (
        <select
          value={item.kanalTipi || ''}
          disabled={updatingChannelCustomerId === item.id}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onChange={async (e) => {
            const nextValue = e.target.value;
            const previousValue = item.kanalTipi || '';
            setData((prev) => prev.map((row) => row.id === item.id ? { ...row, kanalTipi: nextValue } : row));
            setUpdatingChannelCustomerId(item.id);
            try {
              await customerService.updateCustomer(item.id, { kanal_tipi: nextValue || null });
              toast.success('Kanal tipi güncellendi');
            } catch (error: any) {
              setData((prev) => prev.map((row) => row.id === item.id ? { ...row, kanalTipi: previousValue } : row));
              toast.error(error?.message || 'Kanal tipi güncellenemedi');
            } finally {
              setUpdatingChannelCustomerId((current) => current === item.id ? null : current);
            }
          }}
          className="min-w-[140px] rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 disabled:opacity-60"
        >
          <option value="">Seçiniz</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    if (key === 'status') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          item.status === 'Aktif' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {item.status}
        </span>
      );
    }
    if (key === 'customerType') {
      const colors: Record<string, string> = {
        'Yükleme': 'bg-blue-100 text-blue-800',
        'Teslimat': 'bg-purple-100 text-purple-800',
        'Her İkisi': 'bg-green-100 text-green-800',
      };
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[item.customerType] || 'bg-gray-100 text-gray-800'}`}>
          {item.customerType}
        </span>
      );
    }
    const value = item[key as keyof Customer];
    return value === undefined || value === null || value === '' ? '-' : value;
  };

  const renderActions = (item: Customer) => (
    <div className="flex gap-2">
      <button
        onClick={() => openForm(item)}
        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
      >
        <Edit className="w-4 h-4" />
      </button>
      <button
        onClick={() => handleDelete(item.id)}
        className="p-1 text-red-600 hover:bg-red-50 rounded"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );

  const handleOpenCoordinateAudit = async () => {
    try {
      setCoordinateAuditLoading(true);
      setCoordinateAuditOpen(true);
      setCoordinateAuditRows([]);
      setCoordinateAuditProgress(0);
      setCoordinateAuditProcessed(0);
      setCoordinateAuditTotal(0);
      const response: any = await customerService.getCoordinateAudit();
      setCoordinateAuditJobId(String(response?.data?.jobId || ''));
    } catch (error: any) {
      console.error('Coordinate audit load failed:', error);
      toast.error(error?.message || 'Koordinat uyum kontrolü alınamadı');
      setCoordinateAuditRows([]);
      setCoordinateAuditJobId(null);
    } finally {
    }
  };

  useEffect(() => {
    if (!coordinateAuditOpen || !coordinateAuditJobId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const response: any = await customerService.getCoordinateAuditStatus(coordinateAuditJobId);
        const payload = response?.data || {};
        if (cancelled) return;
        setCoordinateAuditProgress(Number(payload.progress || 0));
        setCoordinateAuditProcessed(Number(payload.processed || 0));
        setCoordinateAuditTotal(Number(payload.total || 0));
        if (payload.status === 'completed') {
          setCoordinateAuditRows(Array.isArray(payload.rows) ? payload.rows : []);
          setCoordinateAuditLoading(false);
          return;
        }
        if (payload.status === 'failed') {
          setCoordinateAuditLoading(false);
          toast.error(payload.error || 'Koordinat uyum kontrolü alınamadı');
          return;
        }
        window.setTimeout(poll, 1200);
      } catch (error: any) {
        if (cancelled) return;
        setCoordinateAuditLoading(false);
        toast.error(error?.message || 'Koordinat uyum kontrolü alınamadı');
      }
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [coordinateAuditOpen, coordinateAuditJobId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Müşteri Yönetimi</h1>
          <p className="text-gray-500 mt-1">Yükleme ve teslimat noktası müşterilerini yönetin</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedStokbarCity}
            onChange={(e) => setSelectedStokbarCity(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="">Tüm İller</option>
            {cities.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
          <button
            onClick={handleTransferFromStokbar}
            disabled={stokbarTransferLoading}
            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {stokbarTransferLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            Stokbar Müşteri Aktar
          </button>
          <button onClick={downloadTemplate} className="flex items-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">
            <Download className="w-5 h-5" /> Şablon İndir
          </button>
          <button type="button" onClick={() => void handleOpenCoordinateAudit()} className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 font-semibold text-amber-800 hover:bg-amber-100">
            <AlertTriangle className="w-5 h-5" /> Koordinat Uyum Kontrolü
          </button>
          <button
            type="button"
            onClick={handleUpdateMissingCoordinatesFromOSM}
            disabled={osmUpdating}
            className="flex items-center gap-2 rounded-lg border border-sky-400 bg-sky-200 px-4 py-2 font-semibold !text-black shadow-sm hover:bg-sky-300 disabled:opacity-50"
          >
            {osmUpdating ? <Loader2 className="w-5 h-5 animate-spin" /> : <MapPin className="w-5 h-5" />}
            {osmUpdating ? 'Koordinatlar Güncelleniyor...' : 'Eksik Koordinatları OSM ile Güncelle'}
          </button>
          <button type="button" onClick={handleExcelUploadClick} disabled={importParsing} className="flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg border border-blue-200 hover:bg-blue-100 disabled:opacity-50">
            {importParsing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />} {importParsing ? (parseStatus || 'Excel Okunuyor...') : "Excel'den Yükle"}
          </button>
         <input
  ref={excelImportInputRef}
  type="file"
  accept=".xlsx,.xls,.csv"
  className="hidden"
  onChange={(e) => {
    const file = e.target.files?.[0];

    console.log('Dosya seçildi:', file);

    if (file) {
      handleImport(file);
    }

    e.target.value = '';
  }}
/> <button
            onClick={() => openForm()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Yeni Müşteri
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Toplam Müşteri</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{data.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Aktif Müşteri</div>
          <div className="text-2xl font-bold text-green-600 mt-1">
            {data.filter(c => c.status === 'Aktif').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Yükleme Noktası</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">
            {data.filter(c => c.customerType === 'Yükleme' || c.customerType === 'Her İkisi').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Teslimat Noktası</div>
          <div className="text-2xl font-bold text-purple-600 mt-1">
            {data.filter(c => c.customerType === 'Teslimat' || c.customerType === 'Her İkisi').length}
          </div>
        </div>
      </div>

      {osmUpdating && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-medium text-orange-800">OpenStreetMap koordinat güncellemesi devam ediyor</div>
            <div className="text-sm text-orange-700">{osmProgress.current} / {osmProgress.total}</div>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-orange-100">
            <div
              className="h-3 bg-orange-600 transition-all"
              style={{
                width: osmProgress.total > 0 ? `${Math.round((osmProgress.current / osmProgress.total) * 100)}%` : '0%',
              }}
            />
          </div>
          <div className="mt-2 text-sm text-orange-700">
            Başarılı: {osmProgress.success} | Başarısız: {osmProgress.failed}
          </div>
        </div>
      )}

      {locationDirectoryAvailable && showLocationFilters && <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={applyLocationFilters} disabled={!selectedCity} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Filtrele</button>
          <button onClick={clearLocationFilters} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Filtreyi Temizle</button>
          <div className="text-sm text-gray-500">Seçilen il ve ilçe müşteri tablosuna uygulanır.</div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900">İller</div>
              <div className="text-xs text-gray-500">Toplam {cities.length} kayıt</div>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">İl</th>
                </tr>
              </thead>
              <tbody>
                {cities.map((city) => (
                  <tr
                    key={city.id}
                    onClick={() => handleCitySelect(city)}
                    className={`border-t cursor-pointer hover:bg-blue-50 ${selectedCity?.id === city.id ? 'bg-blue-100' : ''}`}
                  >
                    <td className="px-4 py-2">{city.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900">İlçeler</div>
              <div className="text-xs text-gray-500">
                {selectedCity ? `${selectedCity.name} için ${districts.length} kayıt` : 'Önce il seçin'}
              </div>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">İlçe</th>
                </tr>
              </thead>
              <tbody>
                {districts.map((district) => (
                  <tr
                    key={district.id}
                    onClick={() => handleDistrictSelect(district)}
                    className={`border-t cursor-pointer hover:bg-blue-50 ${selectedDistrict?.id === district.id ? 'bg-blue-100' : ''}`}
                  >
                    <td className="px-4 py-2">{district.name}</td>
                  </tr>
                ))}
                {!districts.length && (
                  <tr>
                    <td className="px-4 py-4 text-sm text-gray-500">İlçe listesi yok</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900">Mahalleler</div>
              <div className="text-xs text-gray-500">
                {selectedDistrict ? `${selectedDistrict.name} için ${neighborhoods.length} kayıt` : 'Önce ilçe seçin'}
              </div>
            </div>
            {locationLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">Mahalle</th>
                </tr>
              </thead>
              <tbody>
                {neighborhoods.map((neighborhood) => (
                  <tr key={neighborhood.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2">{neighborhood.name}</td>
                  </tr>
                ))}
                {!neighborhoods.length && (
                  <tr>
                    <td className="px-4 py-4 text-sm text-gray-500">Mahalle listesi yok</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>}

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-blue-700 font-medium">{selectedIds.size} kayıt seçili</span>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => bulkUpdateStatus('Aktif')} className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700">Aktif Yap</button>
            <button onClick={() => bulkUpdateStatus('Pasif')} className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700">Pasif Yap</button>
            <button onClick={bulkDelete} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700">Sil</button>
            <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded">İptal</button>
          </div>
        </div>
      )}

      <div className="workiom-toolbar !justify-start !pt-0">
        <div className="flex flex-wrap items-center gap-3 w-full">
          <div className="workiom-search max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="Müşteri adı, cari kod, il veya telefon ile ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="outline-none" />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`workiom-outline-button ${showFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}
          >
            <Filter className="w-4 h-4" /> Filtrele
          </button>
          <button
            onClick={() => setShowLocationFilters(!showLocationFilters)}
            className={`workiom-outline-button ${showLocationFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}
          >
            <Filter className="w-4 h-4" /> İl / İlçe / Mahalle
          </button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="workiom-outline-button">
              <XCircle className="w-4 h-4" /> Filtreleri Temizle
            </button>
          )}
          <button type="button" onClick={handleExportExcel} className="workiom-outline-button">
            Excele Aktar
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setShowGrouping((prev) => !prev)} className={`workiom-outline-button ${showGrouping ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}><Filter className="w-4 h-4" /> Gruplandır</button>
            {showGrouping && (
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
                <option value="">Gruplama Yok</option>
                <option value="status">Durum</option>
                <option value="customerType">Müşteri Tipi</option>
                <option value="city">İl</option>
                <option value="district">İlçe</option>
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="workiom-grid-wrap visible-scrollbars border border-gray-200 bg-white" style={{ height: 450, overflow: 'auto' }}>
        <table className="workiom-grid-table text-sm">
            <thead>
              <tr>
                <th className="workiom-grid-index text-left">
                  <input 
                    type="checkbox" 
                    checked={selectedIds.size === filteredData.length && filteredData.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                </th>
                {columns.map((column) => (
                  <th 
                    key={column.key}
                    onClick={() => handleSort(column.key as keyof Customer)}
                    className="text-left cursor-pointer hover:bg-gray-100 select-none"
                  >
                    <div className="flex items-center gap-2">
                      {column.label}
                      {sortField === column.key && (
                        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                ))}
                <th className="workiom-grid-sticky-right text-left">
                  İşlemler
                </th>
              </tr>
              {showFilters && (
                <tr>
                  <th className="workiom-grid-index"></th>
                  {columns.map((column) => (
                    <th key={`filter-${column.key}`}>
                      {column.key === 'city' ? (
                        <select value={filters.city || ''} onChange={(e) => setFilters({ ...filters, city: e.target.value, district: '', neighborhood: '' })} className="w-full rounded border border-gray-300 px-2 py-1 text-xs">
                          <option value="">İl filtre</option>
                          {cityFilterOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>
                      ) : column.key === 'district' ? (
                        <select value={filters.district || ''} onChange={(e) => setFilters({ ...filters, district: e.target.value, neighborhood: '' })} className="w-full rounded border border-gray-300 px-2 py-1 text-xs">
                          <option value="">İlçe filtre</option>
                          {districtFilterOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>
                      ) : column.key === 'neighborhood' ? (
                        <select value={filters.neighborhood || ''} onChange={(e) => setFilters({ ...filters, neighborhood: e.target.value })} className="w-full rounded border border-gray-300 px-2 py-1 text-xs">
                          <option value="">Mahalle filtre</option>
                          {neighborhoodFilterOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>
                      ) : (
                        <input value={filters[column.key as keyof Customer] || ''} onChange={(e) => setFilters({ ...filters, [column.key]: e.target.value })} placeholder={`${column.label} filtre`} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                      )}
                    </th>
                  ))}
                  <th className="workiom-grid-sticky-right"></th>
                </tr>
              )}
            </thead>
            <tbody>
              {Object.entries(groupedCustomers).flatMap(([groupKey, groupItems]) => {
                const rows: any[] = [];
                if (groupBy) {
                  rows.push(
                    <tr key={`group-${groupKey}`} className="bg-gray-100">
                      <td colSpan={columns.length + 2} className="px-3 py-2">
                        <button type="button" onClick={() => setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))} className="flex items-center gap-2 text-sm font-medium text-gray-900">
                          {collapsedGroups[groupKey] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                          <span>{groupKey}</span>
                          <span className="text-gray-500">({groupItems.length})</span>
                        </button>
                      </td>
                    </tr>
                  );
                }

                if (!collapsedGroups[groupKey]) {
                  rows.push(...groupItems.map((item, index) => (
                <tr key={item.id} onDoubleClick={() => openForm(item)} className={`hover:bg-gray-50 cursor-pointer ${selectedIds.has(item.id) ? 'bg-blue-50' : ''}`}>
                  <td className="workiom-grid-index">
                    <input 
                      type="checkbox" 
                      checked={selectedIds.has(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                  </td>
                  {columns.map((column) => (
                    <td key={column.key}>
                      {renderCell(item, column.key)}
                    </td>
                  ))}
                  <td className="workiom-grid-sticky-right">
                    {renderActions(item)}
                  </td>
                </tr>
                  )));
                }
                return rows;
              })}
            </tbody>
          </table>
        
        {filteredData.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz müşteri kaydı yok'}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-h-[90vh] sm:max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white p-4 sm:p-6">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Müşteri Düzenle' : 'Yeni Müşteri Ekle'}
              </h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-y-auto p-4 space-y-6 sm:p-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Temel Bilgiler</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Müşteri Adı *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value.toLocaleUpperCase('tr-TR') })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Firma Ünvanı"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cari Kodu
                    </label>
                    <input
                      type="text"
                      value={formData.code || ''}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toLocaleUpperCase('tr-TR') })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vergi Kimlik No
                    </label>
                    <input
                      type="text"
                      value={formData.taxNumber || ''}
                      onChange={(e) => setFormData({ ...formData, taxNumber: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Müşteri Tipi *
                    </label>
                    <select
                      required
                      value={formData.customerType || 'Her İkisi'}
                      onChange={(e) => setFormData({ ...formData, customerType: e.target.value as Customer['customerType'] })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Yükleme">Yükleme Noktası</option>
                      <option value="Teslimat">Teslimat Noktası</option>
                      <option value="Her İkisi">Her İkisi</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Durum *
                    </label>
                    <select
                      required
                      value={formData.status || 'Aktif'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as Customer['status'] })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Aktif">Aktif</option>
                      <option value="Pasif">Pasif</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Location Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Lokasyon Bilgileri
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      İl *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.city || ''}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value.toLocaleUpperCase('tr-TR') })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      İlçe
                    </label>
                    <input
                      type="text"
                      value={formData.district || ''}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value.toLocaleUpperCase('tr-TR') })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mahalle
                    </label>
                    <input
                      type="text"
                      value={formData.neighborhood || ''}
                      onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value.toLocaleUpperCase('tr-TR') })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adres
                    </label>
                    <input
                      ref={addressInputRef}
                      type="text"
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value.toLocaleUpperCase('tr-TR') })}
                      placeholder="Adres yazarak arayın (otomatik koordinat doldurulur)"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {getGoogleMapsApiKeySync() && (
                      <p className="mt-1 text-xs text-gray-400">Google Places ile otomatik adres, il, ilçe ve koordinat doldurulur</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Enlem (Latitude)
                    </label>
                    <input
                      type="text"
                      value={formData.latitude || ''}
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="40.123456"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Boylam (Longitude)
                    </label>
                    <input
                      type="text"
                      value={formData.longitude || ''}
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="29.123456"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">İletişim Bilgileri</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Yetkili Kişi
                    </label>
                    <input
                      type="text"
                      value={formData.contactPerson || ''}
                      onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value.toLocaleUpperCase('tr-TR') })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0212 123 4567"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-posta
                    </label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kanal Tipi
                    </label>
                    <select
                      value={formData.kanalTipi || ''}
                      onChange={(e) => setFormData({ ...formData, kanalTipi: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seçiniz</option>
                      {(channelOptions.length > 0 ? channelOptions : DEFAULT_CHANNEL_OPTIONS).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Yükleme Başlangıç
                    </label>
                    <input
                      type="time"
                      value={formData.loadingStartTime || ''}
                      onChange={(e) => setFormData({ ...formData, loadingStartTime: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Yükleme Bitiş
                    </label>
                    <input
                      type="time"
                      value={formData.loadingEndTime || ''}
                      onChange={(e) => setFormData({ ...formData, loadingEndTime: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Yükleme Servis Süresi (dk)
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={formData.loadingServiceDurationMinutes ?? 90}
                      onChange={(e) => setFormData({ ...formData, loadingServiceDurationMinutes: Number(e.target.value || 90) })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tahliye Başlangıç
                    </label>
                    <input
                      type="time"
                      value={formData.unloadingStartTime || ''}
                      onChange={(e) => setFormData({ ...formData, unloadingStartTime: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tahliye Bitiş
                    </label>
                    <input
                      type="time"
                      value={formData.unloadingEndTime || ''}
                      onChange={(e) => setFormData({ ...formData, unloadingEndTime: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tahliye Servis Süresi (dk)
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={formData.unloadingServiceDurationMinutes ?? 90}
                      onChange={(e) => setFormData({ ...formData, unloadingServiceDurationMinutes: Number(e.target.value || 90) })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Boşaltma Bekleme Süresi (dk)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={formData.unloadingWaitDurationMinutes ?? 0}
                      onChange={(e) => setFormData({ ...formData, unloadingWaitDurationMinutes: Number(e.target.value || 0) })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notlar
                    </label>
                    <textarea
                      value={formData.notes || ''}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Özel notlar, talimatlar vb."
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={saving}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingItem ? 'Güncelle' : 'Kaydet'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Müşteri Excel Önizleme</h2>
                <p className="text-sm text-gray-600 mt-1">İçe aktarmadan önce satırları kontrol edin</p>
              </div>
              <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4"><div className="text-sm text-emerald-700">Yeni Kayıt</div><div className="text-2xl font-bold text-emerald-900 mt-1">{importPreviewRows.filter((row) => row.importAction === 'create').length}</div></div>
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4"><div className="text-sm text-amber-700">Güncellenecek</div><div className="text-2xl font-bold text-amber-900 mt-1">{importPreviewRows.filter((row) => row.importAction === 'update').length}</div></div>
                <div className="rounded-xl bg-rose-50 border border-rose-200 p-4"><div className="text-sm text-rose-700">Atlanacak Satır</div><div className="text-2xl font-bold text-rose-900 mt-1">{importPreviewRows.filter((row) => row.status === 'error').length}</div></div>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left">Satır</th>
                        <th className="px-4 py-3 text-left">Cari Kodu</th>
                        <th className="px-4 py-3 text-left">Müşteri Adı</th>
                        <th className="px-4 py-3 text-left">İl</th>
                        <th className="px-4 py-3 text-left">İlçe</th>
                        <th className="px-4 py-3 text-left">Telefon</th>
                        <th className="px-4 py-3 text-left">Müşteri Tipi</th>
                        <th className="px-4 py-3 text-left">İşlem</th>
                        <th className="px-4 py-3 text-left">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {importPreviewRows.map((row) => (
                        <tr key={`${row.line}-${row.name}-${row.phone}`} className={row.status === 'error' ? 'bg-rose-50' : ''}>
                          <td className="px-4 py-3">{row.line}</td>
                          <td className="px-4 py-3">{row.code || '-'}</td>
                          <td className="px-4 py-3">{row.name || '-'}</td>
                          <td className="px-4 py-3">{row.city || '-'}</td>
                          <td className="px-4 py-3">{row.district || '-'}</td>
                          <td className="px-4 py-3">{row.phone || '-'}</td>
                          <td className="px-4 py-3">{row.customerType || '-'}</td>
                          <td className="px-4 py-3">
                            {row.status === 'error' ? '-' : (
                              <span className={`font-medium ${row.importAction === 'create' ? 'text-emerald-700' : 'text-amber-700'}`}>
                                {row.importActionLabel}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {row.status === 'ready' ? (
                              <span className="text-emerald-700 font-medium">Hazır</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-rose-700 font-medium"><AlertTriangle className="w-4 h-4" /> {row.reason}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {importResult?.skipped?.length ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                  <h3 className="font-semibold text-rose-900 mb-3">Atlanan Satırlar</h3>
                  <div className="space-y-2 text-sm text-rose-800 max-h-48 overflow-y-auto">
                    {importResult.skipped.map((item, index) => (
                      <div key={`${item.reason}-${index}`} className="border-b border-rose-100 pb-2 last:border-b-0">
                        <div>{item.reason}</div>
                        <div className="text-xs text-rose-700 mt-1">{JSON.stringify(item.row)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {importResult ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                    <div className="text-sm text-emerald-700">Yeni Kaydedilen</div>
                    <div className="text-2xl font-bold text-emerald-900 mt-1">{importResult.imported || 0}</div>
                  </div>
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
                    <div className="text-sm text-amber-700">Guncellenen</div>
                    <div className="text-2xl font-bold text-amber-900 mt-1">{importResult.updated || 0}</div>
                  </div>
                  <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
                    <div className="text-sm text-rose-700">Atlanan</div>
                    <div className="text-2xl font-bold text-rose-900 mt-1">{importResult.skipped?.length || 0}</div>
                  </div>
                  <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
                    <div className="text-sm text-blue-700">Toplam Islenen</div>
                    <div className="text-2xl font-bold text-blue-900 mt-1">{importResult.processed || ((importResult.imported || 0) + (importResult.updated || 0) + (importResult.skipped?.length || 0))}</div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
                <div className="text-sm text-gray-500">Hazır satırlar içe aktarılacak, hatalı satırlar atlanacaktır. Önizlemedeki `İşlem` kolonu hangi satırın yeni kayıt açacağını, hangisinin mevcut kaydı güncelleyeceğini gösterir. Şablonda Enlem ve Boylam kolonları da desteklenir.</div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowImportModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Kapat</button>
                <button
                  onClick={async () => {
                    try {
                      const readyRows = importPreviewRows.filter((row) => row.status === 'ready').map((row) => row.raw);
                      try {
                        const response = await customerService.bulkImport(readyRows);
                        setImportResult(response.data || null);
                      } catch (error: any) {
                        if (error?.status === 404) {
                          const result = await fallbackImportCustomers(readyRows);
                          setImportResult(result);
                        } else {
                          throw error;
                        }
                      }
                      await loadCustomers();
                    } catch (error: any) {
                      console.error('Customer import failed:', error);
                      toast.error(error?.message || 'İçeri aktarma başarısız');
                    }
                  }}
                  disabled={importPreviewRows.filter((row) => row.status === 'ready').length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Hazır Satırları İçe Aktar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showStokbarPreviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Stokbar Müşteri Önizleme</h2>
                <p className="text-sm text-gray-600 mt-1">İller sırayla çekiliyor. Her il için servis yanıtını ve gelen müşteri sayısını aşağıda izleyebilirsiniz.</p>
              </div>
              <button disabled={stokbarImporting} onClick={() => setShowStokbarPreviewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4"><div className="text-sm text-slate-700">Durum</div><div className="text-lg font-bold text-slate-900 mt-1">{stokbarPreviewJobStatus === 'completed' ? 'Tamamlandı' : stokbarPreviewJobStatus === 'failed' ? 'Hata' : stokbarPreviewJobStatus === 'running' ? 'Çalışıyor' : 'Hazırlanıyor'}</div></div>
                <div className="rounded-xl bg-blue-50 border border-blue-200 p-4"><div className="text-sm text-blue-700">Toplam Satır</div><div className="text-2xl font-bold text-blue-900 mt-1">{stokbarPreviewSummary?.total || 0}</div></div>
                <div className="rounded-xl bg-violet-50 border border-violet-200 p-4"><div className="text-sm text-violet-700">Benzersiz Müşteri</div><div className="text-2xl font-bold text-violet-900 mt-1">{stokbarPreviewSummary?.uniqueCustomers || 0}</div></div>
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4"><div className="text-sm text-emerald-700">Aktarılabilir</div><div className="text-2xl font-bold text-emerald-900 mt-1">{stokbarPreviewSummary?.ready || 0}</div></div>
                <div className="rounded-xl bg-rose-50 border border-rose-200 p-4"><div className="text-sm text-rose-700">Hatalı</div><div className="text-2xl font-bold text-rose-900 mt-1">{stokbarPreviewSummary?.error || 0}</div></div>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <div className="font-semibold text-gray-900">İl Bazlı İlerleme</div>
                  {(stokbarPreviewJobStatus === 'queued' || stokbarPreviewJobStatus === 'running') && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                </div>
                <div className="overflow-x-auto max-h-[30vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left">İl</th>
                        <th className="px-4 py-3 text-left">Durum</th>
                        <th className="px-4 py-3 text-left">Gelen Müşteri</th>
                        <th className="px-4 py-3 text-left">Hazır</th>
                        <th className="px-4 py-3 text-left">Hatalı</th>
                        <th className="px-4 py-3 text-left">Mesaj</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {stokbarCityProgress.map((item) => (
                        <tr key={item.cityId}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{item.city}</div>
                            <div className="text-xs text-gray-500">{item.cityAscii}</div>
                          </td>
                          <td className="px-4 py-3">
                            {item.status === 'completed' && <span className="text-emerald-700 font-medium">Tamamlandı</span>}
                            {item.status === 'failed' && <span className="text-rose-700 font-medium">Hata</span>}
                            {item.status === 'running' && <span className="text-blue-700 font-medium">Çekiliyor</span>}
                            {item.status === 'pending' && <span className="text-gray-500 font-medium">Bekliyor</span>}
                          </td>
                          <td className="px-4 py-3">{item.customerCount}</td>
                          <td className="px-4 py-3">{item.readyCount}</td>
                          <td className="px-4 py-3">{item.errorCount}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">{item.message || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 font-semibold text-gray-900">Birleşik Müşteri Önizlemesi</div>
                <div className="overflow-x-auto max-h-[50vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left">Müşteri Kodu</th>
                        <th className="px-4 py-3 text-left">Kaynak İl</th>
                        <th className="px-4 py-3 text-left">Müşteri</th>
                        <th className="px-4 py-3 text-left">Telefon</th>
                        <th className="px-4 py-3 text-left">Ham İl / İlçe / Mahalle</th>
                        <th className="px-4 py-3 text-left">Eşleşen İl / İlçe / Mahalle</th>
                        <th className="px-4 py-3 text-left">Enlem</th>
                        <th className="px-4 py-3 text-left">Boylam</th>
                        <th className="px-4 py-3 text-left">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {[...stokbarPreviewRows]
                        .sort((a, b) => (a.code || '').localeCompare(b.code || '') || (a.name || '').localeCompare(b.name || ''))
                        .map((row, index) => (
                        <tr key={`${row.code}-${row.sourceCityAscii}-${row.name}-${index}`} className={row.status === 'error' ? 'bg-rose-50' : ''}>
                          <td className="px-4 py-3 font-mono font-medium text-gray-900">{row.code || '-'}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{row.sourceCity}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{row.name || '-'}</div>
                            <div className="text-xs text-gray-500">{row.title || '-'}</div>
                          </td>
                          <td className="px-4 py-3">{row.phone || row.email || '-'}</td>
                          <td className="px-4 py-3">{[row.city, row.district, row.neighborhood].filter(Boolean).join(' / ') || '-'}</td>
                          <td className="px-4 py-3">{[row.resolvedCity, row.resolvedDistrict, row.resolvedNeighborhood].filter(Boolean).join(' / ') || '-'}</td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-600">{row.latitude != null ? row.latitude.toFixed(6) : '-'}</td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-600">{row.longitude != null ? row.longitude.toFixed(6) : '-'}</td>
                          <td className="px-4 py-3">
                            {row.status === 'ready'
                              ? <span className="text-emerald-700 font-medium">Hazır</span>
                              : <span className="text-rose-700 font-medium">{row.reason || 'Hata'}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
              <div className="text-sm text-gray-500">{selectedStokbarCity ? `"${selectedStokbarCity}" ili taranıyor.` : 'Tüm iller taranıyor.'} Sonra sadece `Hazır` durumundaki satırlar DB'ye kaydedilir.</div>
              <div className="flex items-center gap-3">
                <button disabled={stokbarImporting} onClick={() => setShowStokbarPreviewModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50">Kapat</button>
                <button
                  onClick={handleImportStokbarPreview}
                  disabled={stokbarImporting || stokbarPreviewJobStatus !== 'completed' || !stokbarPreviewRows.some((row) => row.status === 'ready')}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {stokbarImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Aktar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {coordinateAuditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setCoordinateAuditOpen(false)}>
          <div className="w-full max-w-7xl rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Müşteri / Depo Koordinat Uyum Kontrolü</h2>
                <p className="mt-1 text-sm text-gray-500">OpenStreetMap ile il / ilçe ve varsa adres referansına göre önerilen koordinatlar üretilir; çok uzak kayıtlar hatalı kabul edilir.</p>
              </div>
              <button type="button" onClick={() => setCoordinateAuditOpen(false)} className="rounded-lg p-2 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="max-h-[75vh] overflow-auto p-6">
              {coordinateAuditLoading ? (
                <div className="space-y-4 py-12 text-gray-600">
                  <div className="flex items-center justify-center gap-3"><Loader2 className="w-6 h-6 animate-spin" /> Kontrol çalıştırılıyor...</div>
                  <div className="mx-auto max-w-2xl rounded-2xl border border-amber-200 bg-amber-50 p-4">
                    <div className="mb-2 flex items-center justify-between text-sm font-medium text-amber-900"><span>İlerleme</span><span>%{coordinateAuditProgress}</span></div>
                    <div className="h-3 overflow-hidden rounded-full bg-amber-100"><div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${coordinateAuditProgress}%` }} /></div>
                    <div className="mt-2 text-xs text-amber-800">İşlenen kayıt: {coordinateAuditProcessed} / {coordinateAuditTotal || '-'}</div>
                  </div>
                </div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-100">
                    <tr className="text-left text-gray-700">
                      <th className="px-3 py-2 font-medium">Tip</th>
                      <th className="px-3 py-2 font-medium">Kayıt</th>
                      <th className="px-3 py-2 font-medium">İl / İlçe</th>
                      <th className="px-3 py-2 font-medium">Adres</th>
                      <th className="px-3 py-2 font-medium">Girilen Enlem</th>
                      <th className="px-3 py-2 font-medium">Girilen Boylam</th>
                      <th className="px-3 py-2 font-medium">Önerilen Enlem</th>
                      <th className="px-3 py-2 font-medium">Önerilen Boylam</th>
                      <th className="px-3 py-2 font-medium">Adres Farkı (km)</th>
                      <th className="px-3 py-2 font-medium">İl/İlçe Farkı (km)</th>
                      <th className="px-3 py-2 font-medium">Durum</th>
                      <th className="px-3 py-2 font-medium">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coordinateAuditRows.map((row, index) => (
                      <tr key={`${row.type}-${row.id}-${index}`} className="border-t border-gray-200">
                        <td className="px-3 py-2">{row.type === 'warehouse' ? 'Depo' : 'Müşteri'}</td>
                        <td className="px-3 py-2 font-medium text-gray-900">{row.name}</td>
                        <td className="px-3 py-2">{[row.city, row.district].filter(Boolean).join(' / ') || '-'}</td>
                        <td className="px-3 py-2 max-w-[280px] truncate" title={row.address || '-'}>{row.address || '-'}</td>
                        <td className="px-3 py-2">{row.currentLatitude ?? '-'}</td>
                        <td className="px-3 py-2">{row.currentLongitude ?? '-'}</td>
                        <td className="px-3 py-2">{row.suggestedLatitude ?? row.expectedLatitude ?? '-'}</td>
                        <td className="px-3 py-2">{row.suggestedLongitude ?? row.expectedLongitude ?? '-'}</td>
                        <td className="px-3 py-2 font-semibold">{row.differenceKm != null ? Number(row.differenceKm).toLocaleString('tr-TR') : '-'}</td>
                        <td className="px-3 py-2 font-semibold">{row.districtDifferenceKm != null ? Number(row.districtDifferenceKm).toLocaleString('tr-TR') : '-'}</td>
                        <td className="px-3 py-2">{row.status === 'mismatch' ? <span className="rounded-full bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-700">Uyumsuz</span> : row.status === 'ok' ? <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">Uygun</span> : <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">Eksik</span>}</td>
                        <td className="px-3 py-2">
                          {(row.suggestedLatitude != null || row.expectedLatitude != null) && (row.suggestedLongitude != null || row.expectedLongitude != null) ? (
                            <button type="button" onClick={() => void applySuggestedCoordinates(row)} className="rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100">
                              Önerileni Uygula
                            </button>
                          ) : <span className="text-xs text-gray-400">-</span>}
                        </td>
                      </tr>
                    ))}
                    {coordinateAuditRows.length === 0 ? <tr><td colSpan={12} className="px-3 py-8 text-center text-gray-500">Kayıt bulunamadı.</td></tr> : null}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
